import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';

import CheckoutForm from './CheckoutForm';
import SecondColumn from './SecondColumn';
import ConfirmCheckoutDate from '../../shared/components/ConfirmCheckoutDate';

import Countdown from 'react-countdown';
import {Elements} from '@stripe/react-stripe-js';
import { computeCheckoutCountdown } from '../../../shared/checkoutCountdown';
import { trackXPixelEvent, X_PIXEL_CHECKOUT } from '../utils/xPixel';

const renderer = ({ minutes, seconds }) => {
  var formattedSeconds = seconds.toString();

  if(formattedSeconds.length === 1){
    formattedSeconds = ("0" + formattedSeconds);
  }

  return <span>{minutes}:{formattedSeconds}</span>;
};

const stripePaymentRequired = (ticketReservation) => {
  return ticketReservation.stripe_required;
}

// For 8 minute timer, 37.5% is 3 minutes left
const gettingCloseToExpiration = (checkoutTimePercentageRemaining) => {
  return checkoutTimePercentageRemaining < 37.5;
}

const buildStripeAppearance = (labelColor) => ({
  theme: 'stripe',

  variables: {
    fontFamily: 'Open Sans, system-ui, sans-serif',
    fontSizeBase: "14px",
    borderRadius: '8px',
    colorDanger: "#FF4C00"
  },

  rules: {
      ".Input": {
        boxShadow: "none",
        border: "1px solid #e6e6e6"
      },
      ...(labelColor ? { ".Label": { color: labelColor } } : {})
    }
});

const gtagItems = (confirm, ticketTypes, promoCode) => {
  var item = {
    id: confirm.id,
    name: confirm.title
  }

  return ticketTypes
    .filter((tt) => tt.quantity && tt.quantity > 0)
    .map((tt) => {
      return Object.assign({}, item, {
        quantity: tt.quantity,
        price: parseFloat(tt.calculated_price)
      });
    });
}

const Checkout = ({
  team,
  stripeLabelColor,
  useDefaultTheme,
  widgetTemplateId,
  widgetType,
  showCheckoutDate,
  csrfToken,
  confirm,
  ticketTypes,
  promoCode,
  ticketReservation,
  deleteTicketReservation,
  stripePromise,
  ticketReservationChanged,
  stripeError,
  stripeErrorChanged,
  updateTicketReservation,
  rootUrl,
  checkoutTimePercentageRemaining,
  checkoutTimePercentageRemainingChanged,
  timeLimitReachedChanged,
  isPlacingOrder,
  isPlacingOrderChanged,
  addOns,
  gtag,
  customFieldValues,
  customFieldValueChanged,
  postToParent,
  formatItemsForGoogleTagManager,
  seatsIOSelectedObjects,
  autoSaveTicketReservation,
  userContext,
  saveGeoCodedStateCode,
  verticalInsureClientID,
  registerTicketInsurance,
  insuranceOfferState,
  insuranceOfferStateChanged,
  ticketInsurance,
  deleteTicketInsurance,
  hasInsuranceQuote,
  hasInsuranceQuoteChanged,
  defaultCountry,
  selectedMembershipProduct,
  resaleOrderItems
}) => {
  useEffect(() => {
    window.scrollTo(0, 0);

    gtag('event', 'begin_checkout', {
      "currency": "USD",
      "value": parseFloat(ticketReservation.total),
      "coupon": (Object.keys(promoCode).length > 0 ? promoCode.name : ""),
      "items": gtagItems(confirm, ticketTypes, promoCode)
    });

    var postData = formatItemsForGoogleTagManager(
      confirm,
      promoCode,
      ticketTypes,
      addOns
    );

    postToParent("ga.begin_checkout", postData);

    trackXPixelEvent(X_PIXEL_CHECKOUT, confirm, {
      value: parseFloat(ticketReservation.total),
      numItems: ticketTypes.reduce((count, ticketType) => count + (ticketType.quantity || 0), 0)
    });
  }, []);

  // Anchor the countdown to the client's clock once per reservation so a skewed
  // browser clock can't make it expire instantly (O-2939).
  const countdownRef = useRef({ token: null, target: null, total: null });
  if (countdownRef.current.token !== ticketReservation.token) {
    const { target, total } = computeCheckoutCountdown(ticketReservation.seconds_until_expiry);
    countdownRef.current = { token: ticketReservation.token, target, total };
  }
  const { target: countdownTarget, total: countdownTotal } = countdownRef.current;

  return (
    <div>
      <div className="row">
        <div className="col">
          <p className={`${showCheckoutDate ? "mb-1" : "mb-3"} mt-1 text-dark`}
             style={{"fontSize": "24px"}}>
            <strong>Checkout</strong>
          </p>
          {showCheckoutDate ? (
            <div className="mb-3">
              <ConfirmCheckoutDate confirm={confirm} />
            </div>
          ) : null}
        </div>
        <div className="col-xs-auto"
             style={{"paddingRight": "15px"}}>
          <div className="row mt-2">
            <div className="col small">
              <p className='mb-1'>
                <strong>
                  Time left {
                    <Countdown date={countdownTarget}
                               onTick={
                                 () => {
                                   var remainingTime = countdownTarget - Date.now();
                                   var percentage = (remainingTime / countdownTotal) * 100;
                                   checkoutTimePercentageRemainingChanged(percentage);
                                 }
                               }
                               onComplete={
                                 () => {
                                   checkoutTimePercentageRemainingChanged(0);
                                   deleteTicketReservation(csrfToken, confirm, ticketReservation);
                                   timeLimitReachedChanged(true);
                                 }
                               }
                               renderer={renderer} />
                  }
                </strong>
              </p>
              <div className="progress"
                   style={{
                     "background": (gettingCloseToExpiration(checkoutTimePercentageRemaining) ? "#f9d8ce" : "#d9edb0"),
                     "height": "8px"
                   }}>
                <div className="progress-bar animate-change-to-width"
                     style={{
                       "background": (gettingCloseToExpiration(checkoutTimePercentageRemaining) ? "#ec4926" : "#98c648"),
                       "width": (checkoutTimePercentageRemaining + "%"),
                       "borderTopRightRadius": "8px",
                       "borderBottomRightRadius": "8px"
                     }}
                     role="progressbar"
                     aria-valuenow="0"
                     aria-valuemin="0"
                     aria-valuemax="100">
                </div>
              </div>
            </div>
            <div className="col-xs-auto"
                 style={{"paddingRight": "15px"}}>
              <i className="fas fa-clock mt-1"
                 style={{
                   "fontSize": "24px",
                   "color": (gettingCloseToExpiration(checkoutTimePercentageRemaining) ? "#ec4926" : "#98c648")
                 }}></i>
            </div>
          </div>
        </div>
      </div>
      <div className="row"
           style={{"marginTop": "13px"}}>
        <div className="col-12 col-md-6 col-lg-8 mb-3">
          {stripePaymentRequired(ticketReservation) ? (
            <Elements stripe={stripePromise}
                      options={{
                        clientSecret: ticketReservation.stripe_client_secret,
                        fonts: [{
                          cssSrc: "https://fonts.googleapis.com/css?family=Open+Sans:400,600,700"
                        }],
                        appearance: buildStripeAppearance(stripeLabelColor)
                      }}>
              <CheckoutForm team={team}
                            csrfToken={csrfToken}
                            saveGeoCodedStateCode={saveGeoCodedStateCode}
                            confirm={confirm}
                            ticketReservation={ticketReservation}
                            deleteTicketReservation={deleteTicketReservation}
                            ticketReservationChanged={ticketReservationChanged}
                            stripeError={stripeError}
                            stripeErrorChanged={stripeErrorChanged}
                            updateTicketReservation={updateTicketReservation}
                            rootUrl={rootUrl}
                            ticketTypes={ticketTypes}
                            promoCode={promoCode}
                            isPlacingOrder={isPlacingOrder}
                            isPlacingOrderChanged={isPlacingOrderChanged}
                            stripePaymentRequired={stripePaymentRequired}
                            addOns={addOns}
                            customFieldValues={customFieldValues}
                            customFieldValueChanged={customFieldValueChanged}
                            postToParent={postToParent}
                            seatsIOSelectedObjects={seatsIOSelectedObjects}
                            autoSaveTicketReservation={autoSaveTicketReservation}
                            userContext={userContext}
                            verticalInsureClientID={verticalInsureClientID}
                            registerTicketInsurance={registerTicketInsurance}
                            insuranceOfferState={insuranceOfferState}
                            insuranceOfferStateChanged={insuranceOfferStateChanged}
                            ticketInsurance={ticketInsurance}
                            deleteTicketInsurance={deleteTicketInsurance}
                            hasInsuranceQuote={hasInsuranceQuote}
                            hasInsuranceQuoteChanged={hasInsuranceQuoteChanged}
                            stripePromise={stripePromise}
                            defaultCountry={defaultCountry}
                            selectedMembershipProduct={selectedMembershipProduct}
                            resaleOrderItems={resaleOrderItems}
                            useDefaultTheme={useDefaultTheme}
                            widgetTemplateId={widgetTemplateId}
                            widgetType={widgetType} />
            </Elements>
          ) : (
            <CheckoutForm team={team}
                          csrfToken={csrfToken}
                          saveGeoCodedStateCode={saveGeoCodedStateCode}
                          confirm={confirm}
                          ticketReservation={ticketReservation}
                          deleteTicketReservation={deleteTicketReservation}
                          ticketReservationChanged={ticketReservationChanged}
                          stripeError={stripeError}
                          stripeErrorChanged={stripeErrorChanged}
                          updateTicketReservation={updateTicketReservation}
                          rootUrl={rootUrl}
                          ticketTypes={ticketTypes}
                          promoCode={promoCode}
                          isPlacingOrder={isPlacingOrder}
                          isPlacingOrderChanged={isPlacingOrderChanged}
                          stripePaymentRequired={stripePaymentRequired}
                          addOns={addOns}
                          customFieldValues={customFieldValues}
                          customFieldValueChanged={customFieldValueChanged}
                          postToParent={postToParent}
                          seatsIOSelectedObjects={seatsIOSelectedObjects}
                          autoSaveTicketReservation={autoSaveTicketReservation}
                          userContext={userContext}
                          verticalInsureClientID={""}
                          registerTicketInsurance={registerTicketInsurance}
                          insuranceOfferState={insuranceOfferState}
                          insuranceOfferStateChanged={insuranceOfferStateChanged}
                          ticketInsurance={ticketInsurance}
                          deleteTicketInsurance={deleteTicketInsurance}
                          hasInsuranceQuote={hasInsuranceQuote}
                          hasInsuranceQuoteChanged={hasInsuranceQuoteChanged}
                          stripePromise={stripePromise}
                          defaultCountry={defaultCountry}
                          selectedMembershipProduct={selectedMembershipProduct}
                          resaleOrderItems={resaleOrderItems}
                          useDefaultTheme={useDefaultTheme}
                          widgetTemplateId={widgetTemplateId}
                          widgetType={widgetType} />
          )}
        </div>
        <div className="col-12 col-md-6 col-lg-4 mb-3 order-first order-md-last">
          <SecondColumn confirm={confirm}
                        widgetType={widgetType}
                        ticketTypes={ticketTypes}
                        promoCode={promoCode}
                        addOns={addOns}
                        seatsIOSelectedObjects={seatsIOSelectedObjects}
                        ticketInsurance={ticketInsurance}
                        ticketReservation={ticketReservation}
                        selectedMembershipProduct={selectedMembershipProduct}
                        resaleOrderItems={resaleOrderItems} />
        </div>
      </div>
    </div>
  );
};

Checkout.propTypes = {
  team: PropTypes.object.isRequired,
  useDefaultTheme: PropTypes.bool,
  widgetTemplateId: PropTypes.number,
  widgetType: PropTypes.string,
  showCheckoutDate: PropTypes.bool,
  csrfToken: PropTypes.string.isRequired,
  confirm: PropTypes.object.isRequired,
  ticketTypes: PropTypes.array.isRequired,
  promoCode: PropTypes.object,
  ticketReservation: PropTypes.object,
  deleteTicketReservation: PropTypes.func.isRequired,
  stripePromise: PropTypes.object,
  ticketReservationChanged: PropTypes.func.isRequired,
  stripeError: PropTypes.object,
  stripeErrorChanged: PropTypes.func.isRequired,
  updateTicketReservation: PropTypes.func.isRequired,
  rootUrl: PropTypes.string.isRequired,
  checkoutTimePercentageRemaining: PropTypes.number,
  checkoutTimePercentageRemainingChanged: PropTypes.func.isRequired,
  timeLimitReachedChanged: PropTypes.func.isRequired,
  isPlacingOrder: PropTypes.bool,
  isPlacingOrderChanged: PropTypes.func.isRequired,
  addOns: PropTypes.array,
  gtag: PropTypes.func,
  customFieldValues: PropTypes.array,
  customFieldValueChanged: PropTypes.func.isRequired,
  postToParent: PropTypes.func.isRequired,
  formatItemsForGoogleTagManager: PropTypes.func.isRequired,
  seatsIOSelectedObjects: PropTypes.array,
  autoSaveTicketReservation: PropTypes.func.isRequired,
  userContext: PropTypes.object,
  saveGeoCodedStateCode: PropTypes.func.isRequired,
  verticalInsureClientID: PropTypes.string,
  registerTicketInsurance: PropTypes.func.isRequired,
  insuranceOfferState: PropTypes.string,
  insuranceOfferStateChanged: PropTypes.func.isRequired,
  ticketInsurance: PropTypes.object,
  deleteTicketInsurance: PropTypes.func.isRequired,
  hasInsuranceQuote: PropTypes.bool,
  hasInsuranceQuoteChanged: PropTypes.func.isRequired,
  defaultCountry: PropTypes.string,
  selectedMembershipProduct: PropTypes.object,
  resaleOrderItems: PropTypes.array
};

export default Checkout;
